import { useMemo, useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { EnvIncident, EnvIncidentCategory, EnvIncidentStatus, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const OFFICER_ROLES = ['ENV_OFFICER'];
const CATEGORIES: EnvIncidentCategory[] = ['POLLUTION_WATER', 'POLLUTION_AIR', 'ILLEGAL_DUMPING', 'ILLEGAL_LOGGING', 'OIL_SPILL', 'WILDLIFE_HARM', 'NOISE', 'OTHER'];
const STATUSES: EnvIncidentStatus[] = ['REPORTED', 'UNDER_INVESTIGATION', 'REFERRED', 'RESOLVED', 'DISMISSED'];

/** Public pollution/environmental incident reports — "PUBLIC = any T1+", anonymous allowed (M31). */
export function EnvIncidents() {
  const { persona } = useAuth();
  const isOfficer = persona?.roles.includes('ENV_OFFICER') ?? false;
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showReport, setShowReport] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (category) params.set('category', category);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m31/env-incidents?${params.toString()}`;
  }, [status, category, appliedQ]);
  const listQ = useApiQuery<Page<EnvIncident>>(isOfficer ? path : null);

  return (
    <div>
      <PageHeader
        title="Environmental Incident Reports"
        subtitle="Pollution, illegal dumping/logging, oil spills and wildlife-harm reports — reported -> investigated -> resolved/referred/dismissed (M31)."
        actions={
          <button className="btn-primary" onClick={() => setShowReport(true)}>
            + Log incident report
          </button>
        }
      />

      {isOfficer ? (
        <form
          className="mb-4 flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setAppliedQ(q);
          }}
        >
          <input className="field-input !w-56" placeholder="Ref or description…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="field-input !w-auto" value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">All categories</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
          <button type="submit" className="btn-primary">
            Search
          </button>
        </form>
      ) : null}

      {!isOfficer ? <RoleNotice roles={OFFICER_ROLES} /> : null}
      {isOfficer ? (
        <>
          {listQ.error ? <ErrorNote message={listQ.error} /> : null}
          <div className="card overflow-x-auto">
            {listQ.loading ? (
              <Spinner />
            ) : (listQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No incidents match" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Ref</th>
                    <th className="table-head">Category</th>
                    <th className="table-head">Reporter</th>
                    <th className="table-head">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {listQ.data!.items.map((i) => (
                    <tr key={i.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(i.id)}>
                      <td className="table-cell font-mono text-xs font-medium">{i.refNo}</td>
                      <td className="table-cell">
                        <StatusBadge status={i.category} />
                      </td>
                      <td className="table-cell text-xs text-slate-500">{i.anonymous ? 'Anonymous' : i.reporterName ?? '—'}</td>
                      <td className="table-cell">
                        <StatusBadge status={i.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : null}

      {selectedId ? <IncidentDetailModal id={selectedId} onClose={() => setSelectedId(null)} onChanged={() => listQ.reload()} /> : null}
      {showReport ? (
        <ReportModal
          onClose={() => setShowReport(false)}
          onDone={() => {
            setShowReport(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function IncidentDetailModal({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const q = useApiQuery<EnvIncident>(`/api/v1/m31/env-incidents/${id}`);
  const i = q.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assignedTo, setAssignedTo] = useState('');
  const [outcome, setOutcome] = useState<'RESOLVED' | 'REFERRED' | 'DISMISSED'>('RESOLVED');
  const [notes, setNotes] = useState('');

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      q.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={i ? i.refNo : 'Environmental incident'} onClose={onClose} wide>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {i ? (
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
            <Detail label="Category" value={<StatusBadge status={i.category} />} />
            <Detail label="Status" value={<StatusBadge status={i.status} />} />
            <Detail label="Reporter" value={i.anonymous ? 'Anonymous' : (i.reporterName ?? '—')} />
            {i.locationDescription ? <Detail label="Location" value={i.locationDescription} /> : null}
            {i.gpsLat !== null && i.gpsLng !== null ? <Detail label="GPS" value={`${i.gpsLat.toFixed(5)}, ${i.gpsLng.toFixed(5)}`} mono /> : null}
            {i.assignedTo ? <Detail label="Assigned to" value={i.assignedTo} mono /> : null}
          </div>
          <p className="text-slate-700">{i.description}</p>
          {i.photos && i.photos.length > 0 ? (
            <p className="text-xs text-slate-500">Photos on file: {i.photos.map((p) => p.docRef).join(', ')}</p>
          ) : null}
          {i.investigationNotes ? <p className="text-xs text-slate-500">Investigation notes: {i.investigationNotes}</p> : null}

          {i.status === 'REPORTED' ? (
            <div className="flex gap-2">
              <input className="field-input" placeholder="Assign to (officer subject / team)" value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} />
              <button className="btn-primary" disabled={busy || !assignedTo.trim()} onClick={() => act(() => api.post(`/api/v1/m31/env-incidents/${id}/assign`, { assignedTo }))}>
                Assign
              </button>
            </div>
          ) : null}

          {i.status === 'UNDER_INVESTIGATION' ? (
            <div className="space-y-2 rounded-lg border border-slate-200 p-3">
              <div className="text-sm font-semibold text-slate-700">Close out investigation</div>
              <select className="field-input" value={outcome} onChange={(e) => setOutcome(e.target.value as never)}>
                <option value="RESOLVED">Resolved</option>
                <option value="REFERRED">Referred (to another agency)</option>
                <option value="DISMISSED">Dismissed</option>
              </select>
              <input className="field-input" placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
              <button className="btn-primary" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m31/env-incidents/${id}/resolve`, { outcome, notes: notes || undefined }))}>
                Confirm outcome
              </button>
            </div>
          ) : null}

          <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">Reported {formatDateTime(i.createdAt)}</div>
        </div>
      ) : null}
    </Modal>
  );
}

function ReportModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [anonymous, setAnonymous] = useState(false);
  const [reporterName, setReporterName] = useState('');
  const [reporterContact, setReporterContact] = useState('');
  const [category, setCategory] = useState<EnvIncidentCategory>('POLLUTION_WATER');
  const [description, setDescription] = useState('');
  const [locationDescription, setLocationDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m31/env-incidents', {
        anonymous,
        reporterName: anonymous ? undefined : reporterName,
        reporterContact: anonymous ? undefined : reporterContact || undefined,
        category,
        description,
        locationDescription: locationDescription || undefined,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to log report');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Log environmental incident report" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <p className="text-xs text-slate-500">A counter officer logging a call-in, or a member of the public reporting through this console — reports may be anonymous.</p>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} />
          Anonymous report
        </label>
        {!anonymous ? (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Reporter name">
              <input className="field-input" value={reporterName} onChange={(e) => setReporterName(e.target.value)} />
            </Field>
            <Field label="Reporter contact (optional)">
              <input className="field-input" value={reporterContact} onChange={(e) => setReporterContact(e.target.value)} placeholder="phone or email" />
            </Field>
          </div>
        ) : null}
        <Field label="Category">
          <select className="field-input" value={category} onChange={(e) => setCategory(e.target.value as EnvIncidentCategory)}>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Location description (optional)">
          <input className="field-input" value={locationDescription} onChange={(e) => setLocationDescription(e.target.value)} />
        </Field>
        <Field label="Description">
          <textarea className="field-input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" disabled={busy || description.trim().length < 10 || (!anonymous && !reporterName.trim())} onClick={submit}>
            {busy ? 'Submitting…' : 'Log report'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
